import Button from "@/components/Button"
import Dialog from "@/components/Dialog"
import InputErrorMessage from "@/components/InputErrorMessage"
import { SelectInput, TextareaInput } from "@/components/InputFields"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import { OffboardReason } from "@/model/Prescription"
import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { offboardReasonLabels } from "./offboardReasonLabels"

type OffboardPatientFormData = {
  reason: string
  note: string
}

const formSchema = z.object({
  reason: z.string().min(1, { message: "Reason is required" }),
  note: z.string(),
})

const reasonOptions = [
  { title: "Select a reason...", value: "" as OffboardReason, disabled: true },
  ...Object.entries(offboardReasonLabels).map(([value, label]) => ({
    value: value as OffboardReason,
    title: label,
  })),
]

type OffboardPatientDialogProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: (reason: OffboardReason, note: string) => Promise<void>
  isSubmitting: boolean
}

const OffboardPatientDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting,
}: OffboardPatientDialogProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<OffboardPatientFormData>({
    mode: "onChange",
    defaultValues: {
      reason: "",
      note: "",
    },
    resolver: zodResolver(formSchema),
  })

  useEffect(() => {
    if (!isOpen) {
      reset({
        reason: "",
        note: "",
      })
    }
  }, [isOpen, reset])

  const close = () => {
    onClose()
  }

  const onSubmit = async (formData: OffboardPatientFormData) => {
    await onConfirm(formData.reason as OffboardReason, formData.note)
  }

  return (
    <Dialog
      className="max-w-lg"
      title="Offboard Patient"
      isOpen={isOpen}
      onClose={close}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-4"
        autoComplete="off"
      >
        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="reason">Reason</InputLabel>
          <SelectInput
            id="reason"
            disabled={isSubmitting}
            options={reasonOptions}
            {...register("reason")}
          />
          <InputErrorMessage
            message={errors?.reason?.message}
            className="ml-1"
          />
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel htmlFor="note">Note (optional)</InputLabel>
          <TextareaInput
            id="note"
            disabled={isSubmitting}
            rows={4}
            {...register("note")}
          />
        </div>

        <div className="mt-4 flex items-center justify-end gap-4">
          <Button
            type="button"
            variant="neutral"
            onClick={close}
            disabled={isSubmitting}
            className="px-6"
          >
            Cancel
          </Button>
          <Button
            className="flex items-center gap-3 px-6"
            type="submit"
            variant="danger"
            disabled={isSubmitting}
          >
            {isSubmitting && <Spinner className="h-4 w-4" />}
            Offboard Patient
          </Button>
        </div>
      </form>
    </Dialog>
  )
}

export default OffboardPatientDialog
